import React, { ReactNode, useMemo, useState } from 'react';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';

export interface WorkflowPlacesProps<T extends object> {
  placesToWrite: T[];
  updatePlacesToWrite: (placesToWrite: T[]) => void;
  placeToWriteComponent: ({
    setNewPlaceToWrite,
    newPlaceToWrite,
  }: {
    setNewPlaceToWrite: (place: T | null) => void;
    newPlaceToWrite: T | null;
  }) => ReactNode;
}

export const WorkflowPlaces = <T extends object>({
  placeToWriteComponent,
  placesToWrite,
  updatePlacesToWrite,
}: WorkflowPlacesProps<T>) => {
  const [newPlaceToWrite, setNewPlaceToWrite] = useState<T | null>(null);

  const addBehavior: TouchTargetBehavior = useMemo(() => {
    return {
      type: 'onClick',
      onClick: () => {
        if (!newPlaceToWrite) {
          return;
        }

        updatePlacesToWrite([...placesToWrite, { ...newPlaceToWrite }]);
      },
    };
  }, [newPlaceToWrite, updatePlacesToWrite, placesToWrite]);

  return (
    <div className="space-y-4 p-4 bg-white rounded shadow">
      {placeToWriteComponent({
        setNewPlaceToWrite,
        newPlaceToWrite,
      })}
      <Button
        behavior={addBehavior}
        text={'Add Place'}
        disabled={newPlaceToWrite === null}
        className="mt-4"
      />
      <Table
        items={placesToWrite}
        columnDefinitions={[
          {
            id: 'place',
            header: () => <div>Place</div>,
            cell: ({ item }) => JSON.stringify(item),
          },
          {
            id: 'delete',
            header: () => <div>Delete</div>,
            cell: ({ rowIndex }) => {
              return (
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      const newPlaces = [...placesToWrite];
                      newPlaces.splice(rowIndex, 1);
                      updatePlacesToWrite(newPlaces);
                    },
                  }}
                  variant={'outline'}
                  size={'small'}
                >
                  Delete
                </Button>
              );
            },
          },
        ]}
      />
    </div>
  );
};
